import React, { useEffect, useRef, useState } from "react";

import { enqueueSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import { useGetCommentChartDataMutation } from "store/slices/commentSlice";
import { useDebounceCallback } from "usehooks-ts";
import { publish, subscribe, unsubscribe } from "utils/events";

import FavouribilityChart from "./Chart/FavouribiltyChart";
import CommentsSummary from "./CommentsSummary";
import { Error } from "../components/Error";
import { COMMENTS_APP_DATA_LOADING_EVENT, COMMENTS_APP_FILTERS_APPLIED_EVENT, COMMENTS_APP_FILTERS_CANCEL_EVENT } from "../constants";
import { getCurrentUrlParams, getValues } from "../utils";

const MIN_COMMENTS_COUNT = 10;

const CommentContent = () => {

  const [currentParams] = useSearchParams();
         
  const geoParam = currentParams.get("geo");
  const countryParam = currentParams.get("country");
  const divisionParam = currentParams.get("division");
  const mgmtLvlParam = currentParams.get("mgmtLvl");
  const l2mParam = currentParams.get("l2m");
  const l3mParam = currentParams.get("l3m");
  const favorabilityParam = currentParams.get("favorability");
  const questionParam = currentParams.get("question");
  const searchParam = currentParams.get("search");
  const datesParam = currentParams.get("dates");
  const sentimentParam = currentParams.get("sentiment");
  const tenureParam = currentParams.get("tenure");
    
  const [ getChartData, { isLoading: isChartLoading } ] = useGetCommentChartDataMutation();
  const [favorabilityData, setFavorabilityData] = useState(null);
  const [isServerError, setIsServerError] = useState(false);
  
  const cancelFiltersRef = useRef(false);

  const handleFetchChartData = useDebounceCallback(async (params) => {
    try {
      setFavorabilityData(null);
      publish(`${COMMENTS_APP_DATA_LOADING_EVENT}:Start`);
      const response = await getChartData(params ?? {
        dates: getValues(datesParam),
        favorability: getValues(favorabilityParam),
        question: getValues(questionParam),
        geo: getValues(geoParam),
        country: getValues(countryParam),
        division: getValues(divisionParam),
        mgmtLvl: getValues(mgmtLvlParam),
        l2m: getValues(l2mParam),
        l3m: getValues(l3mParam),
        sentiment: getValues(sentimentParam),
        tenure: getValues(tenureParam),
        ...(searchParam ? { text: searchParam } : {}),
      }).unwrap();

      publish(`${COMMENTS_APP_DATA_LOADING_EVENT}:End`);
      
      if (cancelFiltersRef.current) {
        cancelFiltersRef.current = false;
        return;
      }
      
      setFavorabilityData(response);

      const currentCommentCount = response?.[datesParam]?.total_comments || 0;

      // If not enough comments then we will not load next component
      if (currentCommentCount < MIN_COMMENTS_COUNT) {
        publish(`${COMMENTS_APP_DATA_LOADING_EVENT}:End`);
      }
    } catch (error) {
      console.error({ error });
      setFavorabilityData(null);
      setIsServerError(true);
      publish(`${COMMENTS_APP_DATA_LOADING_EVENT}:End`);
    }
  }, 500);

  useEffect(() => {
    const handleApplyFilters = (autoParams = true) => {
      cancelFiltersRef.current = false;
      const params = autoParams ? getCurrentUrlParams() : undefined;
      if (datesParam) {
        handleFetchChartData(params);
      }
    };

    const handleCancelFilters = () => {
      enqueueSnackbar("Cancelled request. Please change filters and try again", { variant: "info", preventDuplicate: true });
      cancelFiltersRef.current = true;
    };

    subscribe(COMMENTS_APP_FILTERS_APPLIED_EVENT, handleApplyFilters);
    subscribe(COMMENTS_APP_FILTERS_CANCEL_EVENT, handleCancelFilters);
    
    // Initial fetch when component mounts
    handleApplyFilters(false);
    
    return () => {
      if (handleApplyFilters) {
        unsubscribe(COMMENTS_APP_FILTERS_APPLIED_EVENT, handleApplyFilters);
      }

      if (handleCancelFilters) {
        unsubscribe(COMMENTS_APP_FILTERS_CANCEL_EVENT, handleCancelFilters);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentCommentCount = favorabilityData?.[datesParam]?.total_comments || 0;

  if (isServerError) {
    return <Error title="Something is not working at this moment" subtitle="Please change filters or refresh this page" />;
  }

  return <>
    <div className="mb-6">
      <FavouribilityChart isLoading={isChartLoading} data={favorabilityData} /> 
    </div>
    {
      !isChartLoading && favorabilityData && currentCommentCount < MIN_COMMENTS_COUNT ? <div className="mb-6">
        <Error 
          title="Sorry, your query has less than 10 results" 
          subtitle="We do not show 10 comments due to confidentiality. Please adjust your query and try again" 
        />
      </div> : null
    }
    {
      !isChartLoading && favorabilityData && currentCommentCount > MIN_COMMENTS_COUNT ? <div className="mb-6">
        <CommentsSummary />
      </div> : null 
    }
  </>;
};

export default CommentContent;
